/**
 * AI CONTEXT
 * Purpose: core evaluation form fields (skills, trait checkboxes, required feedback rules).
 * Supports: current eval editing and previous eval read-only mode.
 * Depends on: EvaluationFormContext trait/user maps.
 * Used by: EvaluationFormModal and PreviousEvalForm.
 * Refactor: extract repeated validator logic into reusable helper.
 */
import { QuestionCircleOutlined } from "@ant-design/icons"
import { Col, Form, Input, Tooltip, Typography } from "antd"
import { groupBy, map, orderBy } from "lodash"
import { useEffect, useMemo, useState } from "react"

import AnimateInFlex, { ANIMATION_BY_KEY } from "#blocks/AnimateInFlex"
import Flex from "#blocks/Flex"
import CheckboxWrapper from "#blocks/Form/CheckboxWrapper"
import FormattedTextBlock from "#blocks/FormattedTextBlock"
import { Evaluation } from "#classes/Evaluation"
import {
  EVAL_CATEGORIES,
  EVAL_CATEGORY_SORT_ORDER,
} from "#constants/evaluationConstants"
import { useThemeContext } from "#contexts/ThemeContextProvider"
import {
  EvaluationConstructor,
  EvaluationTrait,
} from "#customTypes/composedTypes/evaluationTypes"
import { Uuid } from "#customTypes/globalTypes"
import SkillLevelDropdown from "#forms/SkillLevelDropdown"
import { useGetOneStudent } from "#hooks/jotai/useGetStudents"
import { getIsSkillUp } from "#utils/evaluationHelpers"
import { formatSemester } from "#utils/semesterHelpers"
import { camelToWords } from "#utils/textFormatters"

import useEvaluationFormContext from "./useEvaluationFormContext"

const { TextArea } = Input
const { Text, Title } = Typography

const INFO_BUBBLE_TEXT = `For evaluations:
- Students generally shouldn't lose checkboxes
- Students need to fill out half the checkboxes to move to ^, and all the checkboxes to move to the next level
- You can save a draft at any time, but Submit will mark it ready for approval
- If a student is not being promoted, you need to provide feedback
`

const HiddenFields = () => {
  return (
    <>
      <Form.Item name="id" hidden>
        <Input />
      </Form.Item>
      <Form.Item name="currentSkill" hidden>
        <Input />
      </Form.Item>
      <Form.Item name="status" hidden>
        <Input />
      </Form.Item>
      <Form.Item name="studentId" hidden>
        <Input />
      </Form.Item>
      <Form.Item name="classGroup" hidden>
        <Input />
      </Form.Item>
      <Form.Item name="semester" hidden>
        <Input />
      </Form.Item>
      <Form.Item name="curriculumId" hidden>
        <Input />
      </Form.Item>
      <Form.Item name="submittedBy" hidden>
        <Input />
      </Form.Item>
      <Form.Item name="submittedAt" hidden>
        <Input />
      </Form.Item>
      <Form.Item name="lastUpdatedBy" hidden>
        <Input />
      </Form.Item>
      <Form.Item name="lastUpdatedAt" hidden>
        <Input />
      </Form.Item>
    </>
  )
}

const EvaluationForm = ({
  initialValues,
  evalTraitsById,
  isPreviousEval, // We want to remove all form fields for previous evals
  isDraft = false, // Skip validation when saving draft
}: {
  initialValues: EvaluationConstructor | null
  evalTraitsById?: Record<Uuid, EvaluationTrait>
  isPreviousEval?: boolean
  isDraft?: boolean | (() => boolean)
  handleFinish?: (values: EvaluationConstructor) => void
}) => {
  const form = Form.useFormInstance()
  const theme = useThemeContext()
  const nextSkill = Form.useWatch("nextSkill", form) ?? initialValues?.nextSkill
  const currentSkill =
    Form.useWatch("currentSkill", form) ?? initialValues?.currentSkill
  const classGroup =
    Form.useWatch("classGroup", form) ?? initialValues?.classGroup
  const checkboxesFromForm =
    Form.useWatch("checkboxes", form) ?? initialValues?.checkboxes

  // Calculate skill up based on skill levels
  const isSkillUp = useMemo(() => {
    return getIsSkillUp(currentSkill, nextSkill, classGroup)
  }, [currentSkill, nextSkill, classGroup])

  // We use this only to force a rerender. A regular forced rerender didn't seem to work.
  const [checkboxesToDisplay, setCheckboxesToDisplay] = useState<
    Record<Uuid, boolean>
  >(initialValues?.checkboxes ?? {})

  const student = useGetOneStudent(initialValues?.studentId)

  // Populate form fields when the form is mounted
  useEffect(() => {
    form?.resetFields()
  }, [form])

  // Watch for changes in student's skill level and reset form fields accordingly
  // When current skill is changed elsewhere, checkboxes are also changed
  useEffect(() => {
    if (isPreviousEval) return
    if (!student || !initialValues) return

    const currentDidWell = form.getFieldValue("didWell")
    const currentNeedsWork = form.getFieldValue("needsWork")
    const currentSecretMessage = form.getFieldValue("secretMessage")

    form.resetFields()
    form?.setFieldValue("didWell", currentDidWell)
    form?.setFieldValue("needsWork", currentNeedsWork)
    form?.setFieldValue("secretMessage", currentSecretMessage)
    // Reset the form if initial values change (likely from student skill change)
    // form?.setFieldValue("checkboxes", initialValues?.checkboxes)
    setCheckboxesToDisplay(initialValues?.checkboxes ?? {})
  }, [form, initialValues, isPreviousEval, student])

  const { usersById, traitsById } = useEvaluationFormContext((state) => ({
    usersById: state.usersById,
    traitsById: state.traitsById,
  }))

  const loadedTraitsById = evalTraitsById ?? traitsById

  const evaluationForDisplay = useMemo(() => {
    if (!initialValues) return null
    return new Evaluation(initialValues).getEvaluationForDisplay(
      usersById,
      loadedTraitsById
    )
  }, [initialValues, usersById, loadedTraitsById])

  // Use state not initialValues because user might change next skill in the form
  const checkboxes = map(checkboxesToDisplay, (_, id) => ({
    name: loadedTraitsById[id]?.name,
    checked: !!checkboxesFromForm[id],
    id,
    category: loadedTraitsById[id]?.category,
  }))

  const groupedCheckboxes = groupBy(checkboxes, "category")
  const sortedCheckboxes = [
    groupedCheckboxes[EVAL_CATEGORIES.ART_SKILLS],
    groupedCheckboxes[EVAL_CATEGORIES.CREATIVITY],
    groupedCheckboxes[EVAL_CATEGORIES.SOCIAL_SKILLS],
  ].map((categoryCheckboxes) => orderBy(categoryCheckboxes, "name"))

  return (
    <Flex col grow style={{ maxWidth: "90%" }}>
      <Flex col gap={8}>
        <Title level={4} style={{ display: "flex", alignItems: "center" }}>
          {formatSemester(initialValues?.semester)}
          {!isPreviousEval && (
            <Tooltip title={<FormattedTextBlock text={INFO_BUBBLE_TEXT} />}>
              <QuestionCircleOutlined
                style={{
                  fontSize: 15,
                  color: theme.altColor,
                  marginLeft: 8,
                }}
              />
            </Tooltip>
          )}
        </Title>

        <Flex gap={32} style={{ marginTop: 24 }}>
          <Col span={4}>
            <Flex col gap={8}>
              <Text strong>Current level:</Text>
              <Text>{evaluationForDisplay?.currentSkill}</Text>
            </Flex>
          </Col>

          <Col span={4}>
            {isPreviousEval ? (
              <Flex col gap={8}>
                <Text strong>Next level:</Text>
                <Text>{evaluationForDisplay?.nextSkill}</Text>
              </Flex>
            ) : (
              <SkillLevelDropdown
                label={<Text strong>Next level:</Text>}
                fieldName={"nextSkill"}
                classGroup={initialValues?.classGroup}
              />
            )}
          </Col>

          <Col span={10}>
            <Flex col>
              {isSkillUp
                ? isPreviousEval
                  ? "✅ Promoted"
                  : "✅ Promotion recommended"
                : "❌ Promotion not recommended"}
              {!isPreviousEval && !isSkillUp && (
                <Text type="secondary">
                  (Feedback required if not promoted)
                </Text>
              )}
            </Flex>
          </Col>
        </Flex>

        <Title level={4}>Promotion Requirements</Title>

        {/* Checkboxes */}
        <Col span={22}>
          {EVAL_CATEGORY_SORT_ORDER.map((category, index) => (
            <Flex
              col
              key={`${isPreviousEval ? "previous" : "current"}-evaluation-form-checkboxes-category-${category}`}
            >
              <Text strong>{camelToWords(category)}</Text>
              {sortedCheckboxes[index]?.map(({ name, checked, id }) => {
                return (
                  <AnimateInFlex
                    key={`${isPreviousEval ? "previous" : "current"}-evaluation-checkbox-${id}`}
                    animationKey={id}
                    animation={ANIMATION_BY_KEY.leftSm}
                  >
                    <CheckboxWrapper
                      size="small"
                      name={["checkboxes", id]}
                      label={name}
                      value={checked}
                      disabledTooltip={
                        isPreviousEval ? "Previous evals are locked" : undefined
                      }
                    />
                  </AnimateInFlex>
                )
              })}
            </Flex>
          ))}
        </Col>
      </Flex>

      {/* Feedback */}
      {!isSkillUp && (
        <Flex col gap={8} style={{ marginTop: 24 }}>
          <Title level={4}>Feedback</Title>
          {isPreviousEval ? (
            <Col span={24}>
              <Flex col gap={8}>
                <Text strong>What did the student do well?</Text>
                <Text>{evaluationForDisplay?.didWell}</Text>
                <Text strong>What needs work?</Text>
                <Text>{evaluationForDisplay?.needsWork}</Text>
                <Text strong>Secret message</Text>
                <Text>{evaluationForDisplay?.secretMessage}</Text>
              </Flex>
            </Col>
          ) : (
            <Col span={24}>
              <Form.Item
                name="didWell"
                label="What did the student do well?"
                rules={[
                  {
                    validator: (_, value) => {
                      const isDraftValue =
                        typeof isDraft === "function" ? isDraft() : isDraft
                      if (
                        !isDraftValue &&
                        !isSkillUp &&
                        (!value || value.trim() === "")
                      ) {
                        return Promise.reject(
                          new Error(
                            "Feedback is required when promotion is not recommended"
                          )
                        )
                      }
                      return Promise.resolve()
                    },
                  },
                ]}
              >
                <TextArea autoSize={{ minRows: 3 }} />
              </Form.Item>
              <Form.Item
                name="needsWork"
                label="What needs work?"
                rules={[
                  {
                    validator: (_, value) => {
                      const isDraftValue =
                        typeof isDraft === "function" ? isDraft() : isDraft
                      if (
                        !isDraftValue &&
                        !isSkillUp &&
                        (!value || value.trim() === "")
                      ) {
                        return Promise.reject(
                          new Error(
                            "Feedback is required when promotion is not recommended"
                          )
                        )
                      }
                      return Promise.resolve()
                    },
                  },
                ]}
              >
                <TextArea autoSize={{ minRows: 3 }} />
              </Form.Item>
              <Form.Item
                name="secretMessage"
                label="Secret message"
                rules={[
                  {
                    validator: (_, value) => {
                      const isDraftValue =
                        typeof isDraft === "function" ? isDraft() : isDraft
                      if (
                        !isDraftValue &&
                        !isSkillUp &&
                        (!value || value.trim() === "")
                      ) {
                        return Promise.reject(
                          new Error(
                            "Feedback is required when promotion is not recommended"
                          )
                        )
                      }
                      return Promise.resolve()
                    },
                  },
                ]}
              >
                <TextArea autoSize={{ minRows: 3 }} />
              </Form.Item>
            </Col>
          )}
        </Flex>
      )}

      <HiddenFields />
    </Flex>
  )
}

export default EvaluationForm
